/* eslint-disable prettier/prettier */
import { useEffect, useState } from "react";
import { FolderType } from "../../types/FolderType";
import { FolderService } from "../../services/folder.service";
import { Modal } from "../ui/Modal";
import { Label } from "../ui/Label";
import { Input } from "../ui/Input";
import { CustomSelect } from "../ui/Select";
import { Button } from "../ui/Button";

export const FolderModal = ({
  isOpen,
  onClose,
  onSuccess,
  editingFolder,
  defaultParent,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingFolder?: FolderType | null;
  defaultParent?: FolderType | null;
}) => {
  const [folderName, setFolderName] = useState('');
  const [description, setDescription] = useState('');
  const [parentId, setParentId] = useState<string | undefined>();
  const [options, setOptions] = useState<FolderType[]>([]);
  const [nameError, setNameError] = useState<string>('');
  const [initialState, setInitialState] = useState({
    folderName: '',
    description: '',
    parentId: '',
  });

  const resetForm = () => {
    setFolderName('');
    setDescription('');
    setParentId(undefined);
    setNameError('');
    setInitialState({ folderName: '', description: '', parentId: '' });
  };

  const loadFolders = async () => {
    const res = await FolderService.getAllWithHierarchy();
    const filtered = res.filter((f: { id: string; }) => f.id !== 'uncategorized');
    setOptions(filtered);

    if (editingFolder) {
      const name = editingFolder.folderName;
      const desc = editingFolder.description || '';
      const parent = editingFolder.parent?.id;
      setFolderName(name);
      setDescription(desc);
      setParentId(parent);
      setInitialState({ folderName: name, description: desc, parentId: parent || '' });
    } else if (defaultParent) {
      setParentId(defaultParent.id);
      setInitialState({ folderName: '', description: '', parentId: defaultParent.id });
    } else {
      resetForm();
    }
  };

  useEffect(() => {
    loadFolders();
  }, [editingFolder, defaultParent]);

  const flattenFolders = (folders: FolderType[], prefix = ''): { label: string; value: string }[] =>
    folders.flatMap((folder) => {
      if (folder.id === 'uncategorized') return []; 
      const label = prefix ? `${prefix} > ${folder.folderName}` : folder.folderName;
      const current = { label, value: folder.id };
      const children = folder.subfolders?.length ? flattenFolders(folder.subfolders, label) : [];
      return [current, ...children];
    });

  const collectDescendants = (folder: FolderType): string[] =>
    folder.subfolders?.flatMap((child) => [child.id, ...collectDescendants(child)]) || [];

  const excludedIds = editingFolder ? [editingFolder.id, ...collectDescendants(editingFolder)] : [];

  const parentOptions = flattenFolders(options).filter((f) => !excludedIds.includes(f.value));

  const isUnchanged =
    editingFolder &&
    folderName.trim() === initialState.folderName.trim() &&
    description.trim() === initialState.description.trim() &&
    (parentId || '') === initialState.parentId;

  const handleSubmit = async () => {
    const trimmedName = folderName.trim();

    if (!trimmedName) {
      setNameError('Folder name is required.');
      return;
    }

    const payload = {
      folderName: trimmedName,
      description: description.trim(),
      parentId: parentId || null,
    };

    try {
      let response;
      if (editingFolder) {
        response = await FolderService.update(editingFolder.id, payload);
      } else {
        response = await FolderService.create(payload);
      }

      if (response?.success === false) {
        setNameError(response.message || 'Server rejected the request.');
        return;
      }

      onSuccess();
      resetForm();
      loadFolders();
      onClose();
    } catch (err) {
      console.error('[FolderModal] Error during folder save:', err);
      setNameError('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        resetForm();
        onClose();
      }}
      title={editingFolder ? 'Edit Folder' : 'Create Folder'}
    >
      <Label>Folder Name</Label>
      <Input
        placeholder="Enter folder name"
        value={folderName}
        onChange={(e) => {
          setFolderName(e.target.value);
          if (nameError) setNameError('');
        }}
        error={!!nameError}
      />
      {nameError && <p className="mt-1 text-sm text-red-500">{nameError}</p>}

      <Label className="mt-4">Description</Label>
      <Input
        placeholder="Optional description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <Label className="mt-4">Parent Folder</Label>
      <CustomSelect
        placeholder="Select a parent folder (optional)"
        data={[{ label: 'None', value: '' }, ...parentOptions]}
        value={parentId ?? ''}
        onChange={(value) => setParentId(value || undefined)}
        disabled={!!defaultParent}
      />

      <div className="flex justify-end gap-2 mt-6">
        <Button
          variant="secondary"
          onClick={() => {
            resetForm();
            onClose();
          }}
          className="px-7 sm:px-10"
        >
          Cancel
        </Button>
        <Button
          className="px-7 sm:px-10"
          onClick={handleSubmit}
          disabled={Boolean(editingFolder && isUnchanged)}
        >
          {editingFolder ? 'Save' : 'Create'}
        </Button>
      </div>
    </Modal>
  );
};
